// ============================================================================
// Table Columns: Users
// Key: users
// Generated file. Do not edit manually.
// ============================================================================

// #region Imports
import {
	cellRenderer,
	type EntityTableConfig,
	enumRenderer,
	iconEnumRenderer,
} from '../../entity-config-runtime/entity-table-config'
import type {
	CustomPageDto,
	PauseReasonSummary,
	QueueSummary,
	RoleSummary,
	SkillGroupSummary,
	SkillSummary,
	TeamSummary,
	TeamUserDto,
	UserCustomPageDto,
	UserDto,
	UserPauseReasonDto,
	UserQueueDto,
	UserRoleDto,
	UserSkillDto,
	UserSkillGroupDto,
} from '../api/backstage.schemas'
import {
	ApplicationScope,
	CustomPageIcon,
	EntityScope,
	LinkTarget,
	MediaType,
} from '../api/backstage.schemas'
// #endregion

// #region Entity List Columns
export const usersListColumnsConfig = {
	columns: [
		'firstName',
		'lastName',
		'email',
		cellRenderer('isDisabled', 'boolean'),
		'logonName',
		'extension',
		'customProperty1',
		'customProperty2',
		'customProperty3',
		'customProperty4',
		'customProperty5',
	],
	actions: [
		{
			type: 'link',
			id: 'id',
			label: 'tables_actions_detail',
			icon: 'ArrowRight',
			style: 'link',
			href: (row: UserDto) => `/users/${row.id}/details`,
		},
	],
} as const satisfies EntityTableConfig<UserDto>
// #endregion

// #region Relation List Columns
// #region Relation: CustomPages
export const usersRelationsCustomPagesAssignedColumnsConfig = {
	columns: [
		'userFirstName',
		'userLastName',
		'userEmail',
		'userLogonName',
		'customPageName',
	],
} as const satisfies EntityTableConfig<UserCustomPageDto>
export const usersRelationsCustomPagesAvailableColumnsConfig = {
	columns: [
		'displayName',
		'description',
		'url',
		iconEnumRenderer('icon', CustomPageIcon, 'CustomPageIcon'),
		cellRenderer('enabledForAllUsers', 'boolean'),
		cellRenderer('sortOrder', 'number'),
		enumRenderer('linkTarget', LinkTarget, 'LinkTarget'),
		cellRenderer('iFrameWidth', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<CustomPageDto>
// #endregion

// #region Relation: PauseReasons
export const usersRelationsPauseReasonsAssignedColumnsConfig = {
	columns: [
		'pauseReasonName',
		cellRenderer('pauseReasonColor', 'color'),
		'userFirstName',
		'userLastName',
		'userEmail',
		'userLogonName',
	],
} as const satisfies EntityTableConfig<UserPauseReasonDto>
export const usersRelationsPauseReasonsAvailableColumnsConfig = {
	columns: [
		'name',
		cellRenderer('color', 'color'),
		cellRenderer('sortOrder', 'number'),
		cellRenderer('userCount', 'number'),
		cellRenderer('teamCount', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<PauseReasonSummary>
// #endregion

// #region Relation: Queues
export const usersRelationsQueuesAssignedColumnsConfig = {
	columns: [
		'queueName',
		'queueDescription',
		cellRenderer('isActive', 'boolean'),
		cellRenderer('isRequired', 'boolean'),
		'userEmail',
		'userFirstName',
		'userLastName',
		'userLogonName',
		'modifiedBy',
		enumRenderer('type', MediaType, 'MediaType'),
	],
} as const satisfies EntityTableConfig<UserQueueDto>
export const usersRelationsQueuesAvailableColumnsConfig = {
	columns: [
		'name',
		'description',
		cellRenderer('wrapUpTimeSeconds', 'number'),
		cellRenderer('sortOrder', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
		cellRenderer('userCount', 'number'),
		cellRenderer('contactCodeTreeCount', 'number'),
		enumRenderer('type', MediaType, 'MediaType'),
	],
} as const satisfies EntityTableConfig<QueueSummary>
// #endregion

// #region Relation: Roles
export const usersRelationsRolesAssignedColumnsConfig = {
	columns: [
		'roleName',
		enumRenderer('roleEntityScope', EntityScope, 'EntityScope'),
		enumRenderer('roleApplicationScope', ApplicationScope, 'ApplicationScope'),
		cellRenderer('isRoleDefault', 'boolean'),
		'userFirstName',
		'userLastName',
		'userEmail',
		'userLogonName',
		cellRenderer('isManuallyAssigned', 'boolean'),
	],
} as const satisfies EntityTableConfig<UserRoleDto>
export const usersRelationsRolesAvailableColumnsConfig = {
	columns: [
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
		'name',
		enumRenderer('entityScope', EntityScope, 'EntityScope'),
		enumRenderer('applicationScope', ApplicationScope, 'ApplicationScope'),
		cellRenderer('isDefault', 'boolean'),
		cellRenderer('isBaseRole', 'boolean'),
		'userGroup',
		cellRenderer('permissionCount', 'number'),
		cellRenderer('userCount', 'number'),
		cellRenderer('templateCount', 'number'),
	],
} as const satisfies EntityTableConfig<RoleSummary>
// #endregion

// #region Relation: SkillGroups
export const usersRelationsSkillGroupsAssignedColumnsConfig = {
	columns: [
		'skillGroupName',
		'userEmail',
		'userFirstName',
		'userLastName',
		'userLogonName',
		cellRenderer('isActive', 'boolean'),
		'modifiedBy',
	],
} as const satisfies EntityTableConfig<UserSkillGroupDto>
export const usersRelationsSkillGroupsAvailableColumnsConfig = {
	columns: [
		'name',
		cellRenderer('userCount', 'number'),
		cellRenderer('teamCount', 'number'),
		cellRenderer('skillCount', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<SkillGroupSummary>
// #endregion

// #region Relation: Skills
export const usersRelationsSkillsAssignedColumnsConfig = {
	columns: [
		cellRenderer('skillLevel', 'number'),
		'skillName',
		'userFirstName',
		'userLastName',
		'userEmail',
		'userLogonName',
	],
} as const satisfies EntityTableConfig<UserSkillDto>
export const usersRelationsSkillsAvailableColumnsConfig = {
	columns: [
		'name',
		cellRenderer('userCount', 'number'),
		cellRenderer('teamCount', 'number'),
		cellRenderer('templateCount', 'number'),
		cellRenderer('skillGroupCount', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<SkillSummary>
// #endregion

// #region Relation: Teams
export const usersRelationsTeamsAssignedColumnsConfig = {
	columns: [
		'teamName',
		cellRenderer('isResponsible', 'boolean'),
		cellRenderer('isPrimaryTeam', 'boolean'),
		'userFirstName',
		'userLastName',
		'userEmail',
		'userLogonName',
	],
} as const satisfies EntityTableConfig<TeamUserDto>
export const usersRelationsTeamsAvailableColumnsConfig = {
	columns: [
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
		'name',
		cellRenderer('isDefaultTeam', 'boolean'),
		'userGroup',
		'responsibleUserGroup',
		cellRenderer('userCount', 'number'),
		cellRenderer('queueCount', 'number'),
		cellRenderer('skillCount', 'number'),
		cellRenderer('customPageCount', 'number'),
		cellRenderer('skillGroupCount', 'number'),
	],
} as const satisfies EntityTableConfig<TeamSummary>
// #endregion
// #endregion

// #region Client List Columns
export const usersClientGetCustompagesColumnsConfig = {
	columns: [
		'displayName',
		'description',
		'url',
		iconEnumRenderer('icon', CustomPageIcon, 'CustomPageIcon'),
		cellRenderer('enabledForAllUsers', 'boolean'),
		cellRenderer('sortOrder', 'number'),
		enumRenderer('linkTarget', LinkTarget, 'LinkTarget'),
		cellRenderer('iFrameWidth', 'number'),
		'modifiedBy',
		cellRenderer('modifiedUtc', 'dateTime'),
	],
} as const satisfies EntityTableConfig<CustomPageDto>
// #endregion

// #region Columns Config Export
export const usersColumnsConfig = {
	list: usersListColumnsConfig,
	relations: {
		customPages: {
			assigned: usersRelationsCustomPagesAssignedColumnsConfig,
			available: usersRelationsCustomPagesAvailableColumnsConfig,
		},
		pauseReasons: {
			assigned: usersRelationsPauseReasonsAssignedColumnsConfig,
			available: usersRelationsPauseReasonsAvailableColumnsConfig,
		},
		queues: {
			assigned: usersRelationsQueuesAssignedColumnsConfig,
			available: usersRelationsQueuesAvailableColumnsConfig,
		},
		roles: {
			assigned: usersRelationsRolesAssignedColumnsConfig,
			available: usersRelationsRolesAvailableColumnsConfig,
		},
		skillGroups: {
			assigned: usersRelationsSkillGroupsAssignedColumnsConfig,
			available: usersRelationsSkillGroupsAvailableColumnsConfig,
		},
		skills: {
			assigned: usersRelationsSkillsAssignedColumnsConfig,
			available: usersRelationsSkillsAvailableColumnsConfig,
		},
		teams: {
			assigned: usersRelationsTeamsAssignedColumnsConfig,
			available: usersRelationsTeamsAvailableColumnsConfig,
		},
	},
	client: {
		getCustompages: usersClientGetCustompagesColumnsConfig,
	},
} as const
// #endregion
